// UserLevelPermissionPage.tsx — Co Marca Admin — estilo CategoryPage
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Edit, Trash2, Plus, History, ShieldCheck, ChevronRight, Home, Check, X as XIcon } from 'lucide-react';
import { fetchModelPermissionsByFilter } from '../../../services/modelPermissionsService';
import CreatePermissionModal from '../../../components/ModelPermission/CreatePermissionModal';
import EditPermissionModal from '../../../components/ModelPermission/EditPermissionModal';
import DeletePermissionModal from '../../../components/ModelPermission/DeletePermissionModal';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { useModelPermission } from '../../../hooks/useModelPermission';

interface ModelPermission {
    id: number; user_level: number; model_name: string;
    can_create: boolean; can_read: boolean; can_update: boolean; can_delete: boolean;
}

const colors = {
    primary:        '#c1348b',
    primaryDark:    '#8f1f64',
    secondary:      '#61257b',
    secondaryLight: '#8b3fa8',
    accent:         '#ffd97c',
    accentDark:     '#ea8406',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
.ulp-page { font-family:'DM Sans',sans-serif; }
@keyframes ulpFadeUp { from{opacity:0;transform:translateY(18px);}to{opacity:1;transform:translateY(0);} }
.ulp-fadeup   { animation:ulpFadeUp .5s cubic-bezier(.16,1,.3,1) both; }
.ulp-fadeup-1 { animation:ulpFadeUp .5s .08s cubic-bezier(.16,1,.3,1) both; }
@keyframes spin { to{transform:rotate(360deg);} }
.ulp-btn-primary {
    display:inline-flex;align-items:center;gap:7px;
    background:linear-gradient(135deg,${colors.primary},${colors.primaryDark});
    color:#fff;border:none;border-radius:11px;
    padding:9px 20px;font-size:.83rem;font-weight:800;
    cursor:pointer;font-family:'DM Sans',sans-serif;
    box-shadow:0 4px 14px rgba(193, 52, 139,.25);
    transition:all .22s cubic-bezier(.16,1,.3,1);white-space:nowrap;
}
.ulp-btn-primary:hover { transform:translateY(-2px); box-shadow:0 8px 22px rgba(193, 52, 139,.32); }
.ulp-btn-secondary {
    display:inline-flex;align-items:center;gap:7px;
    background:#fff;border:1.5px solid #e2e8f0;
    color:#374151;border-radius:11px;
    padding:9px 16px;font-size:.83rem;font-weight:700;
    cursor:pointer;font-family:'DM Sans',sans-serif;transition:all .2s;
}
.ulp-btn-secondary:hover { background:#f8fafc; border-color:${colors.secondary}40; color:${colors.secondary}; }
.ulp-table-row { transition:background .15s; }
.ulp-table-row:hover { background:#fafcff; }
.ulp-action-btn { width:30px;height:30px;border-radius:9px;border:none;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;transition:all .2s; }
.ulp-perm-badge {
    display:inline-flex;align-items:center;gap:4px;
    border-radius:7px;padding:3px 9px;font-size:.68rem;font-weight:700;
    white-space:nowrap;
}
.custom-scroll::-webkit-scrollbar { height:6px;width:6px; }
.custom-scroll::-webkit-scrollbar-track { background:transparent; }
.custom-scroll::-webkit-scrollbar-thumb { background-color:rgba(97, 37, 123,0.3);border-radius:20px; }
.custom-scroll::-webkit-scrollbar-thumb:hover { background-color:rgba(97, 37, 123,0.5); }
`;

const PERM_CONFIG = {
    can_create: { label:'Crear',   color: colors.secondary,      bg:`${colors.secondary}12`,      border:`${colors.secondary}25` },
    can_read:   { label:'Ver',     color: colors.secondaryLight,  bg:`${colors.secondaryLight}14`, border:`${colors.secondaryLight}30` },
    can_update: { label:'Editar',  color: colors.accentDark,      bg:`${colors.accentDark}12`,     border:`${colors.accentDark}25` },
    can_delete: { label:'Eliminar',color: colors.primary,         bg:`${colors.primary}10`,        border:`${colors.primary}22` },
} as const;

const UserLevelPermissionPage: React.FC = () => {
    const [permissions, setPermissions]         = useState<ModelPermission[]>([]);
    const [loading, setLoading]                 = useState(true);
    const [isModalOpen, setIsModalOpen]         = useState(false);
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const userLevelId = parseInt(id||'0');
    const [editingPerm, setEditingPerm]         = useState<ModelPermission|null>(null);
    const [deletingPerm, setDeletingPerm]       = useState<ModelPermission|null>(null);
    const [currentPage, setCurrentPage]         = useState(1);
    const itemsPerPage = 10;

    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();
    const currentUserLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('modelpermission', currentUserLevelId);

    const loadPermissions = useCallback(async () => {
        if (!perm?.can_read) { setPermissions([]); setLoading(false); return; }
        try {
            setLoading(true);
            const data = await fetchModelPermissionsByFilter({ user_level: userLevelId });
            setPermissions(data);
        } catch { setPermissions([]); }
        finally { setLoading(false); }
    }, [userLevelId, perm?.can_read]);

    useEffect(() => { loadPermissions(); }, [loadPermissions]);

    const sortedPermissions  = useMemo(() => [...permissions].sort((a,b)=>a.id-b.id), [permissions]);
    const totalPages         = Math.ceil(sortedPermissions.length / itemsPerPage);
    const displayedPermissions = sortedPermissions.slice((currentPage-1)*itemsPerPage, currentPage*itemsPerPage);

    if (perm === null) return (
        <>
            <style>{css}</style>
            <div className="ulp-page" style={{ background:'#fff', minHeight:'40vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <p style={{ color:'#64748b', fontWeight:'600' }}>Cargando permisos...</p>
            </div>
        </>
    );

    return (
        <>
            <style>{css}</style>
            <div className="ulp-page" style={{ background:'#fff', minHeight:'100vh', padding:'clamp(24px,3vw,36px) clamp(20px,4vw,40px)' }}>

                {/* ── HEADER ── */}
                <div className="ulp-fadeup" style={{ marginBottom:'28px' }}>
                    <nav style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'.72rem', fontWeight:'600', color:'#94a3b8', marginBottom:'12px' }}>
                        <a href="/dashboard" style={{ color:'#94a3b8', textDecoration:'none', display:'flex', alignItems:'center', gap:'3px', transition:'color .2s' }}
                            onMouseEnter={e=>e.currentTarget.style.color=colors.primary}
                            onMouseLeave={e=>e.currentTarget.style.color='#94a3b8'}>
                            <Home size={11}/> Dashboard
                        </a>
                        <ChevronRight size={10} color="#e2e8f0"/>
                        <a href="/nivelusuarios" style={{ color:'#94a3b8', textDecoration:'none', transition:'color .2s' }}
                            onMouseEnter={e=>e.currentTarget.style.color=colors.primary}
                            onMouseLeave={e=>e.currentTarget.style.color='#94a3b8'}>
                            Niveles
                        </a>
                        <ChevronRight size={10} color="#e2e8f0"/>
                        <span style={{ color:colors.primary, fontWeight:'700' }}>Permisos</span>
                    </nav>

                    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:'16px' }}>
                        <div>
                            <h1 style={{ fontSize:'clamp(1.5rem,3vw,2rem)', fontWeight:'900', color:'#0f172a', letterSpacing:'-.025em', lineHeight:1.1, marginBottom:'6px' }}>
                                Permisos del <span style={{ color:colors.primary }}>Nivel {userLevelId}</span>
                            </h1>
                            <p style={{ fontSize:'.85rem', color:'#64748b', fontWeight:'500' }}>Gestiona los permisos de acceso por modelo para este nivel</p>
                            <div style={{ display:'flex', gap:'5px', marginTop:'10px' }}>
                                {[colors.primary,colors.secondary,colors.accentDark,colors.secondaryLight].map((c,i)=>(
                                    <div key={i} style={{ height:'3px', width:i===0?'24px':'8px', borderRadius:'3px', background:c }}/>
                                ))}
                            </div>
                        </div>
                        <div style={{ display:'flex', gap:'10px', alignItems:'center', flexWrap:'wrap' }}>
                            <button className="ulp-btn-secondary" onClick={()=>navigate('/nivelusuarios')} title="Historial">
                                <History size={15} color={colors.secondary}/>
                                <span style={{ color:colors.secondary }}>Volver</span>
                            </button>
                            {perm.can_create && (
                                <button className="ulp-btn-primary" onClick={()=>setIsModalOpen(true)}>
                                    <Plus size={15}/> Agregar Permiso
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* ── TABLA ── */}
                <div className="ulp-fadeup-1" style={{ background:'#fff', borderRadius:'20px', border:'1.5px solid #f1f5f9', boxShadow:'0 4px 24px rgba(0,0,0,.04)', overflow:'hidden' }}>

                    <CreatePermissionModal isOpen={isModalOpen} onClose={()=>setIsModalOpen(false)} userLevelId={userLevelId} onPermissionCreated={loadPermissions}/>
                    <EditPermissionModal isOpen={!!editingPerm} onClose={()=>setEditingPerm(null)} userLevelId={userLevelId} initialData={editingPerm||{} as ModelPermission} onPermissionUpdated={loadPermissions}/>
                    <DeletePermissionModal isOpen={!!deletingPerm} onClose={()=>setDeletingPerm(null)} permission={deletingPerm||undefined} onPermissionDeleted={loadPermissions}/>
                    <ExpiredSessionModal isOpen={isSessionExpired} onClose={async()=>{ setIsSessionExpired(false); await logout(); }}/>

                    {/* Contador */}
                    <div style={{ padding:'16px 24px', borderBottom:'1.5px solid #f1f5f9', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                            <div style={{ width:'32px', height:'32px', borderRadius:'10px', background:`${colors.primary}12`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                                <ShieldCheck size={15} color={colors.primary}/>
                            </div>
                            <span style={{ fontSize:'.82rem', fontWeight:'800', color:'#0f172a' }}>{sortedPermissions.length}</span>
                            <span style={{ fontSize:'.78rem', color:'#94a3b8', fontWeight:'500' }}>permisos configurados</span>
                        </div>
                        <span style={{ fontSize:'.72rem', color:'#94a3b8', fontWeight:'600' }}>Página {currentPage} de {totalPages||1}</span>
                    </div>

                    {loading ? (
                        <div style={{ padding:'60px', display:'flex', alignItems:'center', justifyContent:'center', gap:'12px' }}>
                            <div style={{ width:'36px', height:'36px', border:`3px solid ${colors.secondary}`, borderTopColor:'transparent', borderRadius:'50%', animation:'spin .7s linear infinite' }}/>
                            <p style={{ color:'#64748b', fontWeight:'600' }}>Cargando permisos...</p>
                        </div>
                    ) : !perm.can_read ? (
                        <div style={{ padding:'60px 24px', textAlign:'center', color:'#94a3b8' }}>
                            <ShieldCheck size={40} style={{ margin:'0 auto 12px', opacity:.3 }}/>
                            <p style={{ fontWeight:'700' }}>No tienes permiso para ver los permisos de este nivel.</p>
                        </div>
                    ) : (
                        <>
                            <div className="custom-scroll" style={{ overflowX:'auto' }}>
                                <table style={{ width:'100%', borderCollapse:'collapse', fontFamily:"'DM Sans',sans-serif" }}>
                                    <thead>
                                        <tr style={{ background:'#fafcff', borderBottom:'1.5px solid #f1f5f9' }}>
                                            {['Modelo','Crear','Ver','Editar','Eliminar','Acciones'].map((h,i)=>(
                                                <th key={i} style={{ padding:'12px 18px', textAlign: i===0?'left':'center', fontSize:'.68rem', fontWeight:'800', color:'#94a3b8', textTransform:'uppercase', letterSpacing:'.08em', whiteSpace:'nowrap' }}>
                                                    {h}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {displayedPermissions.map(p=>(
                                            <tr key={p.id} className="ulp-table-row" style={{ borderBottom:'1px solid #f8fafc' }}>
                                                {/* Modelo */}
                                                <td style={{ padding:'14px 18px', whiteSpace:'nowrap' }}>
                                                    <span style={{ fontSize:'.85rem', fontWeight:'700', color:'#0f172a' }}>{p.model_name}</span>
                                                </td>
                                                {/* Permisos individuales */}
                                                {(['can_create','can_read','can_update','can_delete'] as const).map(key=>{
                                                    const cfg = PERM_CONFIG[key];
                                                    return (
                                                        <td key={key} style={{ padding:'14px 18px', textAlign:'center' }}>
                                                            {p[key] ? (
                                                                <span className="ulp-perm-badge" style={{ background:cfg.bg, border:`1px solid ${cfg.border}`, color:cfg.color }}>
                                                                    <Check size={10}/> {cfg.label}
                                                                </span>
                                                            ) : (
                                                                <span className="ulp-perm-badge" style={{ background:'#f8fafc', border:'1px solid #e2e8f0', color:'#cbd5e1' }}>
                                                                    <XIcon size={10}/> No
                                                                </span>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                                {/* Acciones */}
                                                <td style={{ padding:'14px 18px', textAlign:'center' }}>
                                                    <div style={{ display:'flex', gap:'6px', justifyContent:'center' }}>
                                                        {perm.can_update && (
                                                            <button className="ulp-action-btn" onClick={()=>setEditingPerm(p)} title="Editar"
                                                                style={{ background:`${colors.secondary}12`, color:colors.secondary }}
                                                                onMouseEnter={e=>{ (e.currentTarget as HTMLButtonElement).style.background=colors.secondary; (e.currentTarget as HTMLButtonElement).style.color='#fff'; }}
                                                                onMouseLeave={e=>{ (e.currentTarget as HTMLButtonElement).style.background=`${colors.secondary}12`; (e.currentTarget as HTMLButtonElement).style.color=colors.secondary; }}>
                                                                <Edit size={14}/>
                                                            </button>
                                                        )}
                                                        {perm.can_delete && (
                                                            <button className="ulp-action-btn" onClick={()=>setDeletingPerm(p)} title="Eliminar"
                                                                style={{ background:`${colors.primary}10`, color:colors.primary }}
                                                                onMouseEnter={e=>{ (e.currentTarget as HTMLButtonElement).style.background=colors.primary; (e.currentTarget as HTMLButtonElement).style.color='#fff'; }}
                                                                onMouseLeave={e=>{ (e.currentTarget as HTMLButtonElement).style.background=`${colors.primary}10`; (e.currentTarget as HTMLButtonElement).style.color=colors.primary; }}>
                                                                <Trash2 size={14}/>
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}

                                        {sortedPermissions.length===0 && (
                                            <tr>
                                                <td colSpan={6} style={{ padding:'60px 24px', textAlign:'center' }}>
                                                    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'12px' }}>
                                                        <div style={{ width:'56px', height:'56px', borderRadius:'16px', background:`${colors.primary}10`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                                                            <ShieldCheck size={24} color={colors.primary} style={{ opacity:.5 }}/>
                                                        </div>
                                                        <p style={{ fontSize:'1rem', fontWeight:'800', color:'#0f172a' }}>No hay permisos configurados</p>
                                                        {perm.can_create && <p style={{ fontSize:'.82rem', color:'#94a3b8' }}>Agrega un permiso para comenzar</p>}
                                                        <div style={{ display:'flex', gap:'5px', marginTop:'4px' }}>
                                                            {[colors.primary,colors.secondary,colors.accentDark,colors.secondaryLight].map((c,i)=>(
                                                                <div key={i} style={{ height:'2px', width:i===0?'16px':'6px', borderRadius:'2px', background:c, opacity:.4 }}/>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {sortedPermissions.length>0 && (
                                <div style={{ borderTop:'1.5px solid #f1f5f9' }}>
                                    <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} itemsPerPage={itemsPerPage} totalItems={sortedPermissions.length}/>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default UserLevelPermissionPage;